import React, { useEffect, useState } from "react";
import { Applogo } from "../../../Routes/ImagePath";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Header from "../../layout/Header";
import Sidebar from "../../layout/Sidebar";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useParams } from "react-router-dom";
import api from "../../../api";
import logo from "../../../assets/img/avatar/logo.png";
import { toast } from "react-toastify";

const SalaryView = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  const { id } = useParams();
  const userId = localStorage.getItem("userId");
  const userType = localStorage.getItem("type");
  const employeeId = userType === "Employee" ? userId : id;

  const employeeCode = localStorage.getItem("employee_code");

  // 1. Fetch available years on mount
  useEffect(() => {
    fetchYears();
  }, []);

  // 2. Whenever selectedYear changes, fetch months
  useEffect(() => {
    if (selectedYear) {
      fetchMonths(selectedYear);
    }
  }, [selectedYear]);

  // 3. Whenever selectedMonth changes (and we do have a year), fetch payroll data
  //    This ensures we only fetch data when both year & month are present.
  useEffect(() => {
    if (selectedYear && selectedMonth) {
      const numericMonth = parseInt(selectedMonth, 10);
      fetchPayrollData(employeeId, selectedYear, numericMonth);
    }
  }, [employeeId, selectedYear, selectedMonth]);

  // Removed the effect that fetches payroll on mount with no year/month
  // because that leads to extra fetches and can cause confusion.

  // Fetch the years
  const fetchYears = async () => {
    try {
      setLoading(true);
      const response = await api.post("/emp-pay-slip-year-list");
      const yearList = response.data.data || [];
      setYears(yearList);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch years. Please try again later.");
      setLoading(false);
    }
  };

  // Fetch the months for a given year
  const fetchMonths = async (year) => {
    try {
      setLoading(true);
      const response = await api.post("/emp-pay-slip-month-list", { year });
      const monthData = response.data.data?.months || {};
      const defaultMonth = response.data.data?.default_month || null;

      // Transform monthData object into an array of { id, name }
      const formattedMonths = Object.entries(monthData).map(([key, value]) => ({
        id: key,
        name: value,
      }));

      setMonths(formattedMonths);

      // If there's a default month from the API, auto-select it
      if (defaultMonth) {
        const defaultMonthKey = Object.keys(monthData).find(
          (key) => monthData[key] === defaultMonth
        );
        if (defaultMonthKey) {
          setSelectedMonth(defaultMonthKey);
        }
      } else {
        // Otherwise, optionally set to empty or first month in the list
        // setSelectedMonth(formattedMonths[0]?.id || "");
      }

      setError(null);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch months. Please try again later.");
      setLoading(false);
    }
  };

  // Fetch the payroll data for selected user, year, and month
  const fetchPayrollData = async (employeeId, year, month) => {
    try {
      setLoading(true);
      const response = await api.post("/emp-payroll-data", {
        employee_id: employeeId,
        year,
        month,
      });
      setPayrollData(response.data);
      setLoading(false);
    } catch (err) {
       
      setLoading(false);
    }
  };

  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    // reset months
    setSelectedMonth("");
  };

  // Download the PDF
  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      const response = await api.post("/download-payslip", {
        employee_code: employeeCode,
        year: selectedYear,
        month: selectedMonth,
      });

      const fileUrl = response.data.file_url;
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      } else {
        throw new Error("File URL not found");
      }
      setIsDownloading(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to download payslip.");
      setIsDownloading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Header />
      <Sidebar />
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Payslip" title="Dashboard" subtitle="Payslip" />
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="yearSelect">Select Year</label>
            <select
              id="yearSelect"
              className="form-control"
              value={selectedYear || ""}
              onChange={handleYearChange}
            >
              <option value="" disabled>
                Select Year
              </option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="monthSelect">Select Month</label>
            <select
              id="monthSelect"
              className="form-control"
              value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={!selectedYear}
            >
              <option value="" disabled>
                Select Month
              </option>
              {months.map((month) => (
                <option key={month.id} value={month.id}>
                  {month.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button onClick={handleDownloadPDF} className="btn btn-primary w-100">
              {isDownloading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  &nbsp;Downloading...
                </>
              ) : (
                "Download Payslip"
              )}
            </button>
          </div>
        </div>

        <div id="salary-slip" className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h2 className="text-uppercase fw-bold">
                Payslip{" "}
                {months.find((m) => m.id === selectedMonth)?.name || ""}{" "}
                {selectedYear}
              </h2>
              <img
                src={logo}
                alt="Logo"
                className="mx-5 mt-2"
                style={{ width: "180px" }}
              />
            </div>
            {loading ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger text-center">{error}</div>
            ) : payrollData ? (
              <>
                {/* Company Info */}
                <div className="mb-3">
                  <h5>
                    <strong>{payrollData.data.company_name}</strong>
                  </h5>
                  <p>
                    {payrollData.data.address_1}
                    <br />
                    {payrollData.data.address_2}
                    <br />
                    {payrollData.data.city}, {payrollData.data?.state},{" "}
                    {payrollData.data.pincode}
                  </p>
                </div>

                {/* Employee Info */}
                <div className="mb-3">
                  <h5 className="text-uppercase">
                    <strong>
                      Employee Name :{" "}
                      {payrollData.data.employee_data?.first_name}{" "}
                      {payrollData.data.employee_data?.father_husband}{" "}
                      {payrollData.data.employee_data?.last_name}
                    </strong>
                  </h5>
                  <div className="table-responsive">
                    <table className="table " style={{ border: "none" }}>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Employee Code</strong>
                            <br />
                            {payrollData?.data?.employee_data?.employee_code ||
                              "N/A"}
                          </td>
                          <td>
                            <strong>Date of Joining</strong>
                            <br />
                            {payrollData.data.employee_data?.date_of_joining}
                          </td>
                          <td>
                            <strong>Department</strong>
                            <br />
                            {
                              payrollData?.data?.employee_data?.department
                                ?.department_name || "N/A"
                            }
                          </td>
                          <td>
                            <strong>Sub Department</strong>
                            <br />
                            N/A
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Designation</strong>
                            <br />
                            {
                              payrollData?.data?.employee_data?.designation
                                ?.designation_name || "N/A"
                            }
                          </td>
                          <td>
                            <strong>Payment Mode</strong>
                            <br />
                            Bank Transfer
                          </td>
                          <td>
                            <strong>Bank</strong>
                            <br />
                            {
                              payrollData?.data?.employee_data?.bank_details
                                ?.bank_name || "N/A"
                            }
                          </td>
                          <td>
                            <strong>Bank IFSC</strong>
                            <br />
                            {
                              payrollData?.data?.employee_data?.bank_details
                                ?.ifsc_code || "N/A"
                            }
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Bank Account</strong>
                            <br />
                            {
                              payrollData?.data?.employee_data?.bank_details
                                ?.account_number || "N/A"
                            }
                          </td>
                          <td>
                            <strong>UAN</strong>
                            <br />
                            {payrollData.data.employee_data?.uan}
                          </td>
                          <td>
                            <strong>PF Number</strong>
                            <br />
                            {payrollData.data.employee_data?.pf_no}
                          </td>
                          <td>
                            <strong>PAN Number</strong>
                            <br />
                            {payrollData.data.employee_data?.pan_no}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Salary Info */}
                <div className="mb-3">
                  <h5 className="text-uppercase">
                    <strong>Salary Details</strong>
                  </h5>
                  <div className="table-responsive">
                    <table
                      className="table table-borderless text-start"
                      style={{ marginBottom: 0, width: "100%" }}
                    >
                      <tbody>
                        <tr style={{ borderBottom: "1px solid #000" }}>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Total Working Days
                            </strong>
                            {
                              payrollData.data.attendance
                                ?.working_days_applicable
                            }
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Weekly Offs
                            </strong>
                            {payrollData.data.attendance?.weekly_off ?? 0}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Holidays
                            </strong>
                            {payrollData.data.attendance?.holidays ?? 0}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Penalized Leaves
                            </strong>
                            {payrollData.data.attendance?.penalty ?? 0}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Paid Leaves
                            </strong>
                            {payrollData.data.attendance?.paid_holiday}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Unpaid Leaves
                            </strong>
                            {payrollData.data.attendance?.absent_day}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Net Payable Days
                            </strong>
                            {payrollData.data.attendance
                              ?.working_days_applicable -
                              payrollData.data.attendance?.absent_day}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <strong
                              style={{ display: "block", marginBottom: "4px" }}
                            >
                              Closing PL/CL
                            </strong>
                            {payrollData.data?.total_CL_PL}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr />
                <div className="row">
                  <div className="col-md-6">
                    <h5>
                      <strong>Earnings</strong>
                    </h5>
                    <table className="table ">
                      <tbody>
                        {payrollData.data.emoluments.map((item) => (
                          <tr key={item.id}>
                            <td>{item.component.component_name}</td>
                            <td className="text-end">
                              {parseFloat(item.value).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td>
                            <strong>Total Earnings (A)</strong>
                          </td>
                          <td className="text-end">
                            <strong>{payrollData.data.gross_pay}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h5>
                      <strong>Taxes & Deductions</strong>
                    </h5>
                    <table className="table ">
                      <tbody>
                        {payrollData.data.deductions.map((item) => (
                          <tr key={item.id}>
                            <td>{item.component.component_name}</td>
                            <td className="text-end">
                              {parseFloat(item.value).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td>
                            <strong>Total Deductions (C)</strong>
                          </td>
                          <td className="text-end">
                            <strong>
                              {payrollData.data.total_deductions}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-3">
                  <h5>
                    <strong>Net Salary</strong>
                  </h5>
                  <p>
                    Net Salary Payable (A - C):{" "}
                    <strong>₹{payrollData.data.net_pay}</strong>
                  </p>
                  <p>
                    Net Salary in Words:{" "}
                    <em>{payrollData.data.amount_in_word}</em>
                  </p>
                  <p className="text-muted">
                    <small>* All amounts displayed are in INR</small>
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center">No payroll data available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryView;
